import { useEffect, useMemo, useRef, useState } from 'react';
import { ROLES, ROLE_META, NOTIFY_DEFAULT_TEMPLATES } from '@technohull/shared';
import { useAdminConfig, useSetConfig, useSendTestNotification, NOTIFY_EVENTS, type NotifyEmailConfig } from '../../features/admin.js';
import { useDirectory } from '../../features/directory.js';
import { RecipientPicker, type DirEntry } from '../../components/RecipientPicker.js';
import { ActionBar, Chip, ErrorState, InlineError, LoadingState, PageHeader } from '../../components/ui.js';
import { IconChevronRight } from '../../components/icons.js';
import { useIsPhone } from '../../lib/media.js';
import { AdminBack } from './AdminHub.js';
import { EmailPreview } from './EmailPreview.js';

const NOTIFY_ROLES = ROLES.filter((r) => r !== 'viewer' && r !== 'readonly');
const clean = (list: string[]) => list.map((x) => x.trim()).filter(Boolean);

/** every event's settings in the shape onSave sends, as one comparable string (roles in any order). */
function snapshot(
  roles: Record<string, string[]>, add: Record<string, string[]>, excl: Record<string, string[]>,
  subj: Record<string, string>, body: Record<string, string>, enabled: Record<string, boolean>,
): string {
  return JSON.stringify(NOTIFY_EVENTS.map((ev) => [
    [...(roles[ev.key] ?? [])].sort(),
    clean(add[ev.key] ?? []),
    clean(excl[ev.key] ?? []),
    (subj[ev.key] ?? '').trim(),
    (body[ev.key] ?? '').trim(),
    enabled[ev.key] !== false,
  ]));
}

// each event (notification) is configured independently: which roles get emailed, plus a per-event
// ADD list (extra recipients) and EXCLUDE list. Sections are collapsible so the long list stays
// scannable, and a test panel lets an admin preview any event's email to a chosen person.
export function AdminNotifications() {
  const { data, isLoading, error, refetch } = useAdminConfig();
  const dir = useDirectory();
  // people to pick: accounts only (a merged-away address is the same person as the account it opens)
  const users: DirEntry[] = useMemo(() => (dir.data?.users ?? []).filter((u) => !u.aliasOf), [dir.data]);
  const save = useSetConfig();
  const test = useSendTestNotification();
  const [eventRoles, setEventRoles] = useState<Record<string, string[]>>({});
  const [enabled, setEnabled] = useState<Record<string, boolean>>({});
  const [addList, setAddList] = useState<Record<string, string[]>>({});
  const [exclList, setExclList] = useState<Record<string, string[]>>({});
  const [subjText, setSubjText] = useState<Record<string, string>>({});
  const [bodyText, setBodyText] = useState<Record<string, string>>({});
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [preview, setPreview] = useState<Record<string, boolean>>({});
  // each event's subject / body field, and which of the two was used last: a clicked {field} goes there
  const fieldEls = useRef(new Map<string, HTMLInputElement | HTMLTextAreaElement>());
  const lastField = useRef<Record<string, 'subject' | 'body'>>({});
  const isPhone = useIsPhone();
  // which server copy the local state was last loaded from: until the effect below has run for it, the
  // half-empty first render must not read as "unsaved"
  const [seededFrom, setSeededFrom] = useState<NotifyEmailConfig | null>(null);

  // test panel
  const [testTo, setTestTo] = useState<string[]>([]);
  const [testEvent, setTestEvent] = useState<string>('');
  const [testMsg, setTestMsg] = useState<string>('');
  const [testFail, setTestFail] = useState(false);

  useEffect(() => {
    const ne = data?.notifyEmail;
    if (!ne) return;
    setEventRoles(ne.eventRoles ?? {});
    const a: Record<string, string[]> = {};
    const x: Record<string, string[]> = {};
    const su: Record<string, string> = {};
    const bo: Record<string, string> = {};
    const en: Record<string, boolean> = {};
    for (const ev of NOTIFY_EVENTS) {
      a[ev.key] = ne.eventRecipients?.[ev.key] ?? [];
      x[ev.key] = ne.eventExcludes?.[ev.key] ?? [];
      su[ev.key] = ne.eventTemplates?.[ev.key]?.subject ?? '';
      bo[ev.key] = ne.eventTemplates?.[ev.key]?.body ?? '';
      en[ev.key] = ne.eventEnabled?.[ev.key] !== false; // default ON
    }
    setEnabled(en);
    setAddList(a);
    setExclList(x);
    setSubjText(su);
    setBodyText(bo);
    setSeededFrom(ne);
  }, [data]);

  // unsaved: local edits differ from data.notifyEmail, compared the way onSave builds the config
  const saved = useMemo(() => {
    const ne = data?.notifyEmail;
    if (!ne) return '';
    const subj: Record<string, string> = {};
    const body: Record<string, string> = {};
    for (const [k, t] of Object.entries(ne.eventTemplates ?? {})) { subj[k] = t?.subject ?? ''; body[k] = t?.body ?? ''; }
    return snapshot(ne.eventRoles ?? {}, ne.eventRecipients ?? {}, ne.eventExcludes ?? {}, subj, body, ne.eventEnabled ?? {});
  }, [data]);
  const current = useMemo(
    () => snapshot(eventRoles, addList, exclList, subjText, bodyText, enabled),
    [eventRoles, addList, exclList, subjText, bodyText, enabled],
  );
  const dirty = !!data?.notifyEmail && seededFrom === data.notifyEmail && current !== saved;

  // any edit after a save clears the "Saved." note, so it never sits next to unsaved changes.
  const edited = () => { if (save.isSuccess) save.reset(); };

  const has = (ev: string, role: string) => (eventRoles[ev] ?? []).includes(role);
  const toggle = (ev: string, role: string) => {
    edited();
    setEventRoles((m) => {
      const set = new Set(m[ev] ?? []);
      if (set.has(role)) set.delete(role);
      else set.add(role);
      return { ...m, [ev]: [...set] };
    });
  };

  const setAllOpen = (v: boolean) => setOpen(Object.fromEntries(NOTIFY_EVENTS.map((ev) => [ev.key, v])));
  const allOpen = NOTIFY_EVENTS.every((ev) => open[ev.key]);
  const toggleOpen = (key: string) => setOpen((m) => ({ ...m, [key]: !m[key] }));

  const onSave = () => {
    const eventRecipients: Record<string, string[]> = {};
    const eventExcludes: Record<string, string[]> = {};
    const eventTemplates: Record<string, { subject: string; body: string }> = {};
    const eventEnabled: Record<string, boolean> = {};
    for (const ev of NOTIFY_EVENTS) {
      eventRecipients[ev.key] = clean(addList[ev.key] ?? []);
      eventExcludes[ev.key] = clean(exclList[ev.key] ?? []);
      eventTemplates[ev.key] = { subject: (subjText[ev.key] ?? '').trim(), body: (bodyText[ev.key] ?? '').trim() };
      eventEnabled[ev.key] = enabled[ev.key] !== false;
    }
    const cfg: NotifyEmailConfig = { eventRoles, eventRecipients, eventExcludes, eventTemplates, eventEnabled };
    save.mutate({ notifyEmail: cfg });
  };

  const sendTest = async () => {
    setTestMsg('');
    setTestFail(false);
    const recips = clean(testTo);
    if (!recips.length) { setTestFail(true); setTestMsg('Pick at least one recipient.'); return; }
    const results = await Promise.all(
      // the chosen event's template as it stands on the page, saved or not
      recips.map((to) => test.mutateAsync({
        to,
        event: testEvent || undefined,
        subject: testEvent ? subjText[testEvent] ?? '' : undefined,
        body: testEvent ? bodyText[testEvent] ?? '' : undefined,
      }).then((r) => !!r.sent).catch(() => false)),
    );
    const ok = results.filter(Boolean).length;
    setTestFail(ok < recips.length);
    setTestMsg(ok === recips.length ? `Sent to ${ok} recipient${ok === 1 ? '' : 's'}.` : `Sent ${ok} of ${recips.length}. Check the mail configuration if this keeps failing.`);
  };

  /** put {field} where the cursor is in the subject or body (whichever was used last; the body if neither). */
  const insertField = (ev: string, field: string) => {
    const used = lastField.current[ev];
    const which = used ?? 'body';
    const el = fieldEls.current.get(`${ev}:${which}`);
    const token = `{${field}}`;
    const cur = (which === 'subject' ? subjText[ev] : bodyText[ev]) ?? '';
    // a field keeps its caret after losing focus to the button; one never used gets it at the end
    const at = used && el ? el.selectionStart ?? cur.length : cur.length;
    const end = used && el ? el.selectionEnd ?? at : at;
    const next = cur.slice(0, at) + token + cur.slice(end);
    edited();
    (which === 'subject' ? setSubjText : setBodyText)((m) => ({ ...m, [ev]: next }));
    requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(at + token.length, at + token.length); });
  };

  return (
    <>
      <AdminBack />
      <PageHeader title="Notifications">
        {data && (
          <>
            {/* phones: one toggle instead of the pair */}
            {isPhone ? (
              <button className="btn btn-sm" onClick={() => setAllOpen(!allOpen)}>{allOpen ? 'Collapse all' : 'Expand all'}</button>
            ) : (
              <>
                <button className="btn btn-sm" onClick={() => setAllOpen(true)}>Expand all</button>
                <button className="btn btn-sm" onClick={() => setAllOpen(false)}>Collapse all</button>
              </>
            )}
            <button className="btn btn-sm btn-primary" disabled={save.isPending} onClick={onSave}>
              {save.isPending ? 'Saving…' : 'Save all'}
            </button>
            {save.isSuccess && <span className="muted">Saved.</span>}
          </>
        )}
      </PageHeader>

      {isLoading && <LoadingState />}
      {error && <ErrorState error={error} onRetry={() => void refetch()} />}
      {save.isError && <InlineError error={save.error} onDismiss={() => save.reset()} />}

      {data && (
        <>
          {/* Test panel — preview any notification's email to a chosen person. */}
          <div className="page-panel notif-test" style={{ marginBottom: 16 }}>
            <h3 className="notif-test-title">Send a test email</h3>
            <p className="muted" style={{ margin: '2px 0 12px', fontSize: 12.5 }}>
              Get a real email in your inbox, with sample data and the wording as it stands on this page, saved or not.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16, alignItems: 'end' }}>
              <div>
                <label className="field-label">Send to</label>
                <RecipientPicker
                  users={users}
                  value={testTo}
                  onChange={setTestTo}
                  placeholder="Pick who receives the test…"
                  ariaLabel="Send to"
                />
              </div>
              <div>
                <label className="field-label">Which notification</label>
                <select className="field" aria-label="Which notification" style={{ width: '100%' }} value={testEvent} onChange={(e) => setTestEvent(e.target.value)}>
                  <option value="">Generic test</option>
                  {NOTIFY_EVENTS.map((ev) => (
                    <option key={ev.key} value={ev.key}>{ev.label}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <button className="btn btn-primary" disabled={test.isPending || testTo.length === 0} onClick={sendTest}>
                  {test.isPending ? 'Sending…' : 'Send test'}
                </button>
                {testMsg && (testFail
                  ? <InlineError error={new Error(testMsg)} onDismiss={() => setTestMsg('')} />
                  : <span className="muted" role="status" style={{ fontSize: 12.5 }}>{testMsg}</span>)}
              </div>
            </div>
          </div>

          {NOTIFY_EVENTS.map((ev, i) => {
            const isOpen = !!open[ev.key];
            const roleCount = (eventRoles[ev.key] ?? []).length;
            const addCount = clean(addList[ev.key] ?? []).length;
            const exclCount = clean(exclList[ev.key] ?? []).length;
            const off = enabled[ev.key] === false;
            const summary = off
              ? 'Turned off'
              : [ev.audience ?? `${roleCount} role${roleCount === 1 ? '' : 's'}`, !ev.audience && addCount ? `${addCount} added` : '', exclCount ? `${exclCount} excluded` : '']
                  .filter(Boolean).join(' · ');
            return (
              <div key={ev.key} className={`page-panel notif-card${off ? ' off' : ''}${isOpen ? '' : ' collapsed'}`} style={{ marginBottom: 12 }}>
                <div className="notif-hd">
                  {/* the whole title zone (number, caret, title, summary) is one expand button: the title's
                      button stretches over it, and the On/Off switch stays a separate, fenced-off target */}
                  <div className="notif-hd-main">
                    <span className="notif-collapse" aria-hidden="true">
                      <span className="notif-num">{i + 1}</span>
                      <IconChevronRight size={14} className={`notif-caret${isOpen ? ' open' : ''}`} />
                    </span>
                    <div className="notif-hd-text">
                      <h3 className="notif-title">
                        <button type="button" className="notif-hd-btn" aria-expanded={isOpen} onClick={() => toggleOpen(ev.key)}>{ev.label}</button>
                      </h3>
                      {isOpen ? <p className="notif-desc">{ev.desc}</p> : <div className="notif-summary">{summary}</div>}
                    </div>
                  </div>
                  <label className="notif-toggle" title={off ? 'Turned off - no email or bell is sent' : 'On - sending as configured'}>
                    <input
                      type="checkbox"
                      aria-label={`Send ${ev.label}`}
                      checked={!off}
                      onChange={(e) => { edited(); setEnabled((m) => ({ ...m, [ev.key]: e.target.checked })); }}
                    />
                    <span>{off ? 'Off' : 'On'}</span>
                  </label>
                </div>

                {isOpen && (
                  <>
                    {off && (
                      <div className="notif-off-note">This notification is turned off - no email or in-app alert is sent for it, whatever the settings below say.</div>
                    )}
                    <div className="notif-body" style={off ? { opacity: 0.5 } : undefined}>
                      {ev.audience ? (
                        // a fixed audience (the person it's about): no roles or extra addresses to pick
                        <p className="notif-audience"><span className="field-label">Goes to</span> {ev.audience}, on the bell and by email.</p>
                      ) : (<>
                      <div className="field-label" id={`roles-${ev.key}`}>Notify these roles <span className="muted" style={{ fontWeight: 400 }}>(bell and email)</span></div>
                      <div role="group" aria-labelledby={`roles-${ev.key}`} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '4px 0 16px' }}>
                        {NOTIFY_ROLES.map((r) => (
                          <Chip key={r} active={has(ev.key, r)} onClick={() => toggle(ev.key, r)}>
                            {ROLE_META[r]?.label ?? r}
                          </Chip>
                        ))}
                      </div>
                      </>)}

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
                        {!ev.audience && <div>
                          <label className="field-label">Also email <span className="muted" style={{ fontWeight: 400 }}>(extra people or addresses, email only)</span></label>
                          <RecipientPicker
                            users={users}
                            value={addList[ev.key] ?? []}
                            onChange={(next) => { edited(); setAddList((m) => ({ ...m, [ev.key]: next })); }}
                            placeholder="Search people, or type an email…"
                            ariaLabel={`${ev.label}: also email`}
                          />
                        </div>}
                        <div>
                          <label className="field-label">Exclude <span className="muted" style={{ fontWeight: 400 }}>(never notify these)</span></label>
                          <RecipientPicker
                            users={users}
                            value={exclList[ev.key] ?? []}
                            onChange={(next) => { edited(); setExclList((m) => ({ ...m, [ev.key]: next })); }}
                            allowFragments
                            placeholder="Search people, or type an address / name fragment…"
                            ariaLabel={`${ev.label}: exclude`}
                          />
                        </div>
                      </div>

                      <div style={{ marginTop: 16 }}>
                        <label className="field-label">Email message <span className="muted" style={{ fontWeight: 400 }}>- leave blank to send the default shown in grey</span></label>
                        <input
                          className="field"
                          ref={(el) => { if (el) fieldEls.current.set(`${ev.key}:subject`, el); }}
                          onFocus={() => { lastField.current[ev.key] = 'subject'; }}
                          style={{ width: '100%', marginBottom: 6 }}
                          value={subjText[ev.key] ?? ''}
                          aria-label={`${ev.label} email subject`}
                          onChange={(e) => { edited(); setSubjText((m) => ({ ...m, [ev.key]: e.target.value })); }}
                          placeholder={NOTIFY_DEFAULT_TEMPLATES[ev.key]?.subject ?? 'Custom subject (optional)'}
                        />
                        {/* phones: the default subject runs past a narrow field's placeholder, so spell it out */}
                        {!(subjText[ev.key] ?? '').trim() && NOTIFY_DEFAULT_TEMPLATES[ev.key]?.subject && (
                          <div className="muted notif-default">Default: {NOTIFY_DEFAULT_TEMPLATES[ev.key]?.subject}</div>
                        )}
                        <textarea
                          className="field"
                          ref={(el) => { if (el) fieldEls.current.set(`${ev.key}:body`, el); }}
                          onFocus={() => { lastField.current[ev.key] = 'body'; }}
                          style={{ width: '100%', minHeight: 120, fontFamily: 'inherit' }}
                          value={bodyText[ev.key] ?? ''}
                          aria-label={`${ev.label} email body`}
                          onChange={(e) => { edited(); setBodyText((m) => ({ ...m, [ev.key]: e.target.value })); }}
                          placeholder={NOTIFY_DEFAULT_TEMPLATES[ev.key]?.body ?? 'Custom body (optional). Line breaks are kept.'}
                        />
                        <div className="notif-fields">
                          <span className="muted">Insert a field:</span>
                          {ev.fields.map((f) => (
                            <button key={f} type="button" className="notif-field" onClick={() => insertField(ev.key, f)}>{`{${f}}`}</button>
                          ))}
                        </div>
                        <p className="muted notif-format">
                          A blank line starts a new paragraph. Two or more “Label: value” lines become a table, lines starting with “•” a list, and a line whose fields are all empty is left out.
                        </p>
                        <button type="button" className="btn btn-sm" aria-expanded={!!preview[ev.key]} onClick={() => setPreview((m) => ({ ...m, [ev.key]: !m[ev.key] }))}>
                          {preview[ev.key] ? 'Hide preview' : 'Preview the email'}
                        </button>
                        {preview[ev.key] && <EmailPreview event={ev.key} subject={subjText[ev.key] ?? ''} body={bodyText[ev.key] ?? ''} />}
                      </div>
                    </div>
                  </>
                )}
              </div>
            );
          })}

          <button className="btn btn-primary" disabled={save.isPending} onClick={onSave}>
            {save.isPending ? 'Saving…' : 'Save all'}
          </button>
          {save.isSuccess && <span className="muted" style={{ marginLeft: 10 }}>Saved.</span>}
        </>
      )}
      {/* phones: edits deep in a card have a visible "unsaved" state and a save within reach */}
      <ActionBar show={dirty} message="Unsaved changes">
        <button className="btn btn-primary" disabled={save.isPending} onClick={onSave}>{save.isPending ? 'Saving…' : 'Save all'}</button>
      </ActionBar>
    </>
  );
}
